"use client";

import { useTranslations } from "next-intl";

import {
  ANY_ROLE,
  ROLE_OPTIONS,
  type RoleSelectValue,
} from "@/shared/lib/lol-roles";
import { Checkbox } from "@/shared/ui/inputs/checkbox";
import { Label } from "@/shared/ui/forms/label";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/inputs/select";
import { Textarea } from "@/shared/ui/inputs/textarea";

/** Лимит заметки — совпадает с ограничением бэкенда (PlayerNote, 200 символов). */
export const PLAYER_NOTE_MAX = 200;

interface RoleSelectProps {
  id: string;
  value: RoleSelectValue;
  onChange: (value: RoleSelectValue) => void;
  label: string;
  /** Подпись пункта «Любая» — у основной и дополнительной роли смысл разный. */
  anyLabel: string;
}

function RoleSelect({ id, value, onChange, label, anyLabel }: RoleSelectProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={(v) => onChange(v as RoleSelectValue)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ROLE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              <span className="flex items-center gap-2">
                <RoleIcon role={option.icon} size={15} />
                <RoleLabel labelKey={option.labelKey} />
              </span>
            </SelectItem>
          ))}
          {/* Radix Select не принимает пустую строку как значение, поэтому
              «отсутствие роли» выражено токеном ANY_ROLE. На бэкенд он не уходит. */}
          <SelectItem value={ANY_ROLE}>{anyLabel}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function RoleLabel({ labelKey }: { labelKey: string }) {
  const t = useTranslations("Tournaments");
  return <>{t(`actions.${labelKey}`)}</>;
}

export interface RegistrationRoleFieldsValue {
  mainRole: RoleSelectValue;
  secondaryRole: RoleSelectValue;
  wantsCaptain: boolean;
  playerNote: string;
}

interface RegistrationRoleFieldsProps {
  value: RegistrationRoleFieldsValue;
  onChange: (patch: Partial<RegistrationRoleFieldsValue>) => void;
  /** Капитанство и заметка есть только в драфте — в остальных форматах их некуда деть. */
  showCaptainAndNote: boolean;
  idPrefix?: string;
}

/**
 * Поля заявки, общие для регистрации и правки: роли, пожелание капитанства, заметка.
 *
 * Раньше эти поля существовали в трёх местах (модалка «Участвовать», страница
 * /register, диалог «Моя заявка») и разошлись: где-то не было дополнительной роли,
 * где-то заметки. Из-за этого у части участников одного турнира дополнительная роль
 * была, а у части нет — просто в зависимости от того, каким путём регистрировались.
 */
export function RegistrationRoleFields({
  value,
  onChange,
  showCaptainAndNote,
  idPrefix = "reg",
}: RegistrationRoleFieldsProps) {
  const t = useTranslations("Tournaments");
  const anyRoleChosen = value.mainRole === ANY_ROLE;

  return (
    <div className="space-y-4 rounded-xl bg-muted/30 p-4">
      <RoleSelect
        id={`${idPrefix}-main-role`}
        value={value.mainRole}
        label={t("actions.mainRole")}
        anyLabel={t("actions.roleAny")}
        onChange={(role) =>
          onChange({
            mainRole: role,
            // Капитану нужна конкретная линия — его роль резервирует слот команды,
            // и бэкенд отклоняет капитана без роли. Гасим галочку здесь, чтобы человек
            // не упёрся в отказ уже после отправки.
            ...(role === ANY_ROLE ? { wantsCaptain: false } : {}),
          })
        }
      />
      {anyRoleChosen ? (
        <p className="-mt-2 text-xs text-muted-foreground">
          {t("actions.roleAnyHint")}
        </p>
      ) : null}

      <RoleSelect
        id={`${idPrefix}-secondary-role`}
        value={value.secondaryRole}
        label={t("actions.secondaryRole")}
        anyLabel={t("actions.secondaryRoleNone")}
        onChange={(role) => onChange({ secondaryRole: role })}
      />

      {showCaptainAndNote && (
        <>
          <div className="flex items-start gap-2.5">
            <Checkbox
              id={`${idPrefix}-wants-captain`}
              checked={value.wantsCaptain}
              disabled={anyRoleChosen}
              onCheckedChange={(v) => onChange({ wantsCaptain: v === true })}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor={`${idPrefix}-wants-captain`} className="cursor-pointer">
                {t("actions.wantCaptain")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {anyRoleChosen
                  ? t("actions.wantCaptainNeedsRole")
                  : t("actions.wantCaptainHint")}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-player-note`}>{t("actions.playerNote")}</Label>
            <Textarea
              id={`${idPrefix}-player-note`}
              value={value.playerNote}
              onChange={(e) =>
                onChange({ playerNote: e.target.value.slice(0, PLAYER_NOTE_MAX) })
              }
              placeholder={t("actions.playerNotePlaceholder")}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              {t("actions.playerNoteHint", { count: value.playerNote.length })}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
